import { useEffect, useState } from 'react';
import { activityReadLimit, listPartnerActivity } from '@/data/activityRepo';
import type { ActivityType } from '@/types/types_activity';

/**
 * The instant a window of `days` starts, as an ISO string, or undefined for "all
 * time" — which has no start, so the query gets no lower bound.
 *
 * Anchored to midnight in the reader's timezone rather than `days × 24h` ago, so
 * that "last 7 days" means seven calendar days on the chart and the leftmost
 * column is a whole day like every other.
 */
function windowStart(days: number | null): string | undefined {
  if (days === null) return undefined;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  return start.toISOString();
}

interface LoadedActivityType {
  /** Which request this answers — the partner and window it was read for. */
  key: string;
  events: ActivityType[];
  error: string | null;
}

/**
 * One partner's activity over the last `days` days — or all of it, for `null` —
 * newest first. Admins only: the rules refuse the query to anyone else, which
 * surfaces here as the error.
 *
 * Re-reads when the window changes rather than filtering a wider fetch in
 * memory: the range picker is the only thing that moves, and a year-long read
 * for a partner that is looking at a week is the more expensive of the two.
 *
 * `loading` is derived from which request the held result answers rather than
 * being a flag of its own. That makes a switch of range or partner read as
 * loading from the render that asks for it, instead of showing the previous
 * partner's numbers for a frame under the new partner's name.
 */
export function usePartnerActivity(partnerId: string, days: number | null) {
  const [loaded, setLoaded] = useState<LoadedActivityType | null>(null);
  const key = `${partnerId}|${days ?? 'all'}`;

  useEffect(() => {
    let cancelled = false;
    listPartnerActivity(partnerId, windowStart(days))
      .then((events) => {
        if (!cancelled) setLoaded({ key, events, error: null });
      })
      .catch((e: unknown) => {
        console.error('Activity read failed', e);
        if (!cancelled) setLoaded({ key, events: [], error: 'Could not load activity.' });
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId, days, key]);

  const current = loaded?.key === key ? loaded : null;

  return {
    events: current?.events ?? [],
    loading: !current,
    error: current?.error ?? null,
    /** The read hit its cap, so the oldest events are missing from this window. */
    truncated: (current?.events.length ?? 0) >= activityReadLimit,
  };
}
